import { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Container, Grid, Typography } from '@material-ui/core';
import useAuth from '../../hooks/useAuth';
import {
  FinanceStatistiqueFormationsSuperAdmin,
  FinanceStatistiqueFormationsSubscribedSuperAdmin,
} from '../../components/dashboard/finance';
import { dashboardApi } from '../../api/dashboardApi';
import useMounted from '../../hooks/useMounted';
import * as React from 'react';
import gtm from '../../lib/gtm';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Tab, useTheme } from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import PowerIcon from '@mui/icons-material/Power';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { alpha } from '@mui/material/styles';
import { grey, lightVeil, darkVeil } from '../../theme/tones';
import usePermissions from '../../hooks/usePermissions';

const Analysis = () => {
  const { user } = useAuth();
  const { can } = usePermissions();
  const mounted = useMounted();
  const theme = useTheme();
  const [formationsInfos, setFormationsInfos] = useState([]);
  const [sourcesInfos, setSourcesInfos] = useState([]);
  const [relanceInfos, setRelanceInfos] = useState([]);
  const [professions, setProfessions] = useState([]);
  const [sessionsEndDate, setSessionsEndDate] = useState([]);

  const [tabs, setTabs] = React.useState('analysis');

  const handleChange = (event, tab) => {
    setTabs(tab);
  };

  useEffect(() => {
    gtm.push({ event: 'page_view' });
  }, []);

  return (
    <>
      <Helmet>
        <title>Analyse | Syndays</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: theme.palette.mode === 'light' ? grey(50) : grey(900),
          minHeight: '100%',
          py: 4,
        }}
      >
        <Container maxWidth={false} disableGutters sx={{ px: 2 }}>

          {/* ── Hero Header ── */}
          <Box
            sx={{
              mb: 4,
              p: 3.5,
              borderRadius: '24px',
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
              backdropFilter: 'blur(20px)',
              boxShadow: theme.palette.mode === 'light'
                ? `0 4px 24px ${darkVeil(0.04)}`
                : `0 4px 24px ${darkVeil(0.2)}`,
              display: 'flex',
              alignItems: 'center',
              gap: 3,
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '16px',
                background: (theme) => `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.tones.indigo.base} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: (theme) => `0 8px 20px ${alpha(theme.palette.tones.indigo.base, 0.3)}`,
                flexShrink: 0,
              }}
            >
              <AssessmentIcon sx={{ color: grey(0), fontSize: '1.7rem' }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: '1.5rem',
                  color: 'text.primary',
                  letterSpacing: '-0.02em',
                  lineHeight: 1.2,
                }}
              >
                Tableau d&apos;analyse commerciale
              </Typography>
              <Typography
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  mt: 0.5,
                }}
              >
                Vue consolidée du chiffre d&apos;affaires, des formations et des apprenants inscrits
              </Typography>
            </Box>
            {/* KPI chip */}
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: '12px',
                backgroundColor: (theme) => alpha(theme.palette.success.main, 0.08),
                border: (theme) => `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
              }}
            >
              <TrendingUpIcon sx={{ color: 'success.main', fontSize: '1rem' }} />
              <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'success.main' }}>
                Données en temps réel
              </Typography>
            </Box>
          </Box>

          {/* ── Access Guard ── */}
          {can('analysis.view', user.id_service === 0 || user.id_service === 4) ? (
            <Box>
              <Box sx={{ width: '100%' }}>
                <TabContext value={tabs}>
                  {/* ── Tab navigation ── */}
                  <Box
                    sx={{
                      mb: 3,
                      p: 0.75,
                      borderRadius: '16px',
                      backgroundColor: 'action.hover',
                      border: '1px solid',
                      borderColor: 'divider',
                      backdropFilter: 'blur(10px)',
                      display: 'flex',
                      width: 'fit-content',
                      mx: 'auto',
                      gap: 0.5,
                    }}
                  >
                    <TabList
                      onChange={handleChange}
                      aria-label="Onglets analyse"
                      sx={{
                        minHeight: 'unset',
                        '& .MuiTabs-indicator': { display: 'none' },
                        '& .MuiTabs-flexContainer': { gap: '4px' },
                      }}
                    >
                      <Tab
                        icon={<BarChartIcon sx={{ fontSize: '1rem' }} />}
                        iconPosition="start"
                        label="Analyses"
                        value="analysis"
                        sx={{
                          textTransform: 'none',
                          fontWeight: 700,
                          fontSize: '0.88rem',
                          minHeight: '40px',
                          borderRadius: '12px',
                          color: 'text.secondary',
                          px: 2,
                          py: 0.75,
                          gap: 0.75,
                          transition: 'all 0.2s',
                          '&.Mui-selected': {
                            color: grey(0),
                            backgroundColor: 'secondary.main',
                            boxShadow: (theme) => `0 4px 12px ${alpha(theme.palette.secondary.main, 0.35)}`,
                          },
                          '&:hover:not(.Mui-selected)': {
                            backgroundColor: theme.palette.mode === 'light'
                              ? lightVeil(0.8)
                              : lightVeil(0.06),
                          },
                        }}
                      />
                      <Tab
                        icon={<PowerIcon sx={{ fontSize: '1rem' }} />}
                        iconPosition="start"
                        label="Power BI"
                        value="power-bi"
                        sx={{
                          textTransform: 'none',
                          fontWeight: 700,
                          fontSize: '0.88rem',
                          minHeight: '40px',
                          borderRadius: '12px',
                          color: 'text.secondary',
                          px: 2,
                          py: 0.75,
                          gap: 0.75,
                          transition: 'all 0.2s',
                          '&.Mui-selected': {
                            color: grey(0),
                            backgroundColor: 'secondary.main',
                            boxShadow: (theme) => `0 4px 12px ${alpha(theme.palette.secondary.main, 0.35)}`,
                          },
                          '&:hover:not(.Mui-selected)': {
                            backgroundColor: theme.palette.mode === 'light'
                              ? lightVeil(0.8)
                              : lightVeil(0.06),
                          },
                        }}
                      />
                    </TabList>
                  </Box>

                  {/* ── Tab: Analyses ── */}
                  <TabPanel value="analysis" sx={{ p: 0 }}>
                    <Grid container spacing={3} direction="column">
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            borderRadius: '24px',
                            border: '1px solid',
                            borderColor: 'divider',
                            backgroundColor: 'background.paper',
                            backdropFilter: 'blur(20px)',
                            boxShadow: (theme) => `0 8px 32px ${alpha(theme.palette.common.black, 0.02)}`,
                            overflow: 'hidden',
                          }}
                        >
                          <FinanceStatistiqueFormationsSuperAdmin
                            sessionsEndDate={sessionsEndDate}
                            formationsInfos={formationsInfos}
                            sourcesInfos={sourcesInfos}
                            relanceInfos={relanceInfos}
                            professions={professions}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            borderRadius: '24px',
                            border: '1px solid',
                            borderColor: 'divider',
                            backgroundColor: 'background.paper',
                            backdropFilter: 'blur(20px)',
                            boxShadow: (theme) => `0 8px 32px ${alpha(theme.palette.common.black, 0.02)}`,
                            overflow: 'hidden',
                          }}
                        >
                          <FinanceStatistiqueFormationsSubscribedSuperAdmin
                            sessionsEndDate={sessionsEndDate}
                            formationsInfos={formationsInfos}
                            sourcesInfos={sourcesInfos}
                            relanceInfos={relanceInfos}
                            professions={professions}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </TabPanel>

                  {/* ── Tab: Power BI ── */}
                  <TabPanel
                    value="power-bi"
                    sx={{
                      p: 0,
                      height: '85vh',
                    }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        borderRadius: '24px',
                        border: '1px solid',
                        borderColor: 'divider',
                        overflow: 'hidden',
                        boxShadow: (theme) => `0 8px 32px ${alpha(theme.palette.common.black, 0.04)}`,
                      }}
                    >
                      <iframe
                        title="SYNA_CRM_DASHBOARD"
                        style={{ height: '100%', width: '100%', border: 'none', display: 'block' }}
                        src="https://app.powerbi.com/reportEmbed?reportId=d2145beb-eb39-4ccc-a17f-bd7d0f4420e2&autoAuth=true&ctid=fa3275f0-e320-4358-b729-c0a9880b875c"
                        frameBorder="0"
                        allowFullScreen="true"
                      />
                    </Box>
                  </TabPanel>
                </TabContext>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                p: 4,
                borderRadius: '24px',
                border: '1px solid',
                borderColor: (theme) => alpha(theme.palette.error.main, 0.2),
                background: (theme) => alpha(theme.palette.error.main, 0.04),
                textAlign: 'center',
              }}
            >
              <Typography sx={{ color: 'error.main', fontWeight: 700, fontSize: '0.95rem' }}>
                Vous n&apos;avez pas les droits pour voir l&apos;analyse.
              </Typography>
            </Box>
          )}
        </Container>
      </Box>
    </>
  );
};

export default Analysis;
